import { apiv1 as api } from '@/adapters';
import type {
  EnrollmentCredentialModel,
  EnrollmentCredentialUseModel,
  IssueEnrollmentJoinCodeResult,
  CreateEnrollmentSecretResult as CreateEnrollmentSecretResultModel,
  RotateEnrollmentSecretResult as RotateEnrollmentSecretResultModel,
} from '@/adapters/apiv1.g';
import {
  extractEnrollmentCredential,
  extractEnrollmentUse,
  unwrapData,
} from '@/adapters/shared';
import type {
  CreateEnrollmentSecretPayload,
  CreateEnrollmentSecretResult,
  EnrollmentCredential,
  EnrollmentCredentialKind,
  EnrollmentCredentialUse,
  IssueJoinCodePayload,
  IssueJoinCodeResult,
  RotateEnrollmentSecretResult,
} from '@/types/core/enrollment';
import type { UUID } from '@/utils/uuid';

export const enrollmentService = {
  listCredentials: async (
    tenantId: UUID,
    kind?: EnrollmentCredentialKind
  ): Promise<EnrollmentCredential[]> => {
    const res = await api.listEnrollmentCredentials(
      tenantId,
      kind ? { kind } : undefined
    );
    const raw = unwrapData<EnrollmentCredentialModel[]>(res);
    return (raw ?? []).map(extractEnrollmentCredential);
  },

  listCredentialUses: async (
    tenantId: UUID,
    credentialId: UUID
  ): Promise<EnrollmentCredentialUse[]> => {
    const res = await api.listEnrollmentCredentialUses(tenantId, credentialId);
    const raw = unwrapData<EnrollmentCredentialUseModel[]>(res);
    return (raw ?? []).map(extractEnrollmentUse);
  },

  issueJoinCode: async (
    tenantId: UUID,
    payload: IssueJoinCodePayload
  ): Promise<IssueJoinCodeResult> => {
    const res = await api.issueEnrollmentJoinCode(tenantId, {
      capabilities: payload.capabilities ?? [],
      groups: payload.groups ?? [],
      role_ids: (payload.roleIds ?? []).map(String),
      expires_at: payload.expiresAt,
    });
    const raw = unwrapData<IssueEnrollmentJoinCodeResult>(res);
    if (!raw) throw new Error('No data returned from join code issuance');
    return {
      credentialId: (raw.credential_id ?? '') as UUID,
      code: raw.code ?? '',
      expiresAt: raw.expires_at ?? '',
    };
  },

  createSecret: async (
    tenantId: UUID,
    payload: CreateEnrollmentSecretPayload
  ): Promise<CreateEnrollmentSecretResult> => {
    const res = await api.createEnrollmentSecret(tenantId, {
      name: payload.name,
      capabilities: payload.capabilities ?? [],
      groups: payload.groups ?? [],
      role_ids: (payload.roleIds ?? []).map(String),
      expires_at: payload.expiresAt,
    });
    const raw = unwrapData<CreateEnrollmentSecretResultModel>(res);
    if (!raw) throw new Error('No data returned from secret creation');
    return {
      credentialId: (raw.credential_id ?? '') as UUID,
      name: raw.name ?? payload.name,
      secret: raw.secret ?? '',
    };
  },

  revokeCredential: async (
    tenantId: UUID,
    credentialId: UUID
  ): Promise<void> => {
    await api.revokeEnrollmentCredential(tenantId, credentialId);
  },

  rotateSecret: async (
    tenantId: UUID,
    credentialId: UUID
  ): Promise<RotateEnrollmentSecretResult> => {
    const res = await api.rotateEnrollmentSecret(tenantId, credentialId);
    const raw = unwrapData<RotateEnrollmentSecretResultModel>(res);
    if (!raw) throw new Error('No data returned from secret rotation');
    return {
      credentialId: (raw.credential_id ?? credentialId) as UUID,
      secret: raw.secret ?? '',
    };
  },
};
